'use client';

import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { User, Mail, Shield, X } from 'lucide-react';

export interface UserRole {
  user: string;
  role: string;
}

export interface UserRolesListProps {
  assignments: UserRole[];
  selectedUser?: string | null;
  onClearSelection?: () => void;
  onUnassign?: (user: string, role: string) => void;
}

export function UserRolesList({
  assignments,
  selectedUser,
  onClearSelection,
  onUnassign
}: UserRolesListProps) {
  // Group assignments by user
  const userRolesMap = assignments.reduce((acc, { user, role }) => {
    if (!acc[user]) {
      acc[user] = [];
    }
    acc[user].push(role);
    return acc;
  }, {} as Record<string, string[]>);

  const users = Object.keys(userRolesMap);
  const filteredUsers = selectedUser ? users.filter(u => u === selectedUser) : users;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-blue-600" />
              {selectedUser ? `Roles for ${selectedUser}` : 'All User Roles'}
            </CardTitle>
            <CardDescription>
              {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''} with role assignments
            </CardDescription>
          </div>
          {selectedUser && onClearSelection && (
            <Button variant="outline" size="sm" onClick={onClearSelection}>
              <X className="h-4 w-4 mr-1" />
              Clear Selection
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {filteredUsers.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            {selectedUser
              ? 'This user has no role assignments.'
              : 'No role assignments found.'}
          </div>
        ) : (
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead>User</TableHead>
                  <TableHead>Assigned Roles</TableHead>
                  {onUnassign && <TableHead className="w-[100px]">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map(user => {
                  const roles = userRolesMap[user];
                  return (
                    <TableRow key={user}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="p-2 rounded-full bg-slate-100">
                            {user.includes('@') ? (
                              <Mail className="h-4 w-4 text-slate-500" />
                            ) : (
                              <User className="h-4 w-4 text-slate-500" />
                            )}
                          </div>
                          <span className="font-medium">{user}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {roles.map(role => (
                            <div key={role} className="flex items-center gap-1">
                              <Badge
                                variant="secondary"
                                className="capitalize bg-blue-50 text-blue-700 border-blue-200"
                              >
                                <Shield className="h-3 w-3 mr-1" />
                                {role}
                              </Badge>
                              {onUnassign && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-5 w-5 text-slate-400 hover:text-red-500"
                                  onClick={() => onUnassign(user, role)}
                                >
                                  <X className="h-3 w-3" />
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>
                      </TableCell>
                      {onUnassign && (
                        <TableCell>
                          <span className="text-xs text-slate-400">
                            {roles.length} role{roles.length !== 1 ? 's' : ''}
                          </span>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
